// src/components/VideoLogin.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Andaman & Nicobar Islands", "Chandigarh", "Dadra & Nagar Haveli", "Daman & Diu",
  "Delhi", "Jammu & Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];

const VideoLogin = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showStateSelect, setShowStateSelect] = useState(false);
  const [selectedState, setSelectedState] = useState("");
  const [stateSearch, setStateSearch] = useState("");
  const [inputFocus, setInputFocus] = useState({ username: false, password: false });
  const [buttonHover, setButtonHover] = useState(false);
  const [attempts, setAttempts] = useState(0);

  const setDemoAuthFlags = () => {
    localStorage.setItem("videoKyc_logged_in", "1");
    localStorage.setItem("videoKyc_token", "demo_token_for_video_kyc");
    localStorage.setItem("user_logged_in", "1");
    localStorage.setItem("videoKyc_role", "user");
    try {
      sessionStorage.setItem("isAuthenticated", "true");
    } catch (e) {
      console.warn("Could not set sessionStorage isAuthenticated", e);
    }
    if (remember) {
      localStorage.setItem("videoKyc_remember", "1");
    } else {
      localStorage.removeItem("videoKyc_remember");
    }
  };

  useEffect(() => {
    try {
      if (sessionStorage.getItem("isAuthenticated") === "true") {
        navigate("/home", { replace: true });
      }
    } catch (e) {
      console.warn("sessionStorage read failed", e);
    }
  }, [navigate]);

  useEffect(() => {
    const styleId = "shake-keyframe";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        @keyframes shakeError {
          0%,100% { transform: translateX(0); }
          15%      { transform: translateX(-8px); }
          30%      { transform: translateX(8px); }
          45%      { transform: translateX(-6px); }
          60%      { transform: translateX(6px); }
          75%      { transform: translateX(-3px); }
          90%      { transform: translateX(3px); }
        }
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.5; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 600);
  };

  const API_BASE = process.env.REACT_APP_API_BASE || "";

  const getErrorMessage = (code, attempts) => {
    if (code === "ADMIN_NOT_ALLOWED") {
      return {
        icon: "🚫",
        title: "Admin access not permitted here",
        detail: "This portal is for User login only. Please use User credentials.",
      };
    }
    if (attempts >= 3) {
      return {
        icon: "⚠️",
        title: "Multiple failed attempts",
        detail: "Please double-check your User credentials and try again.",
      };
    }
    return {
      icon: "✕",
      title: "Invalid credentials",
      detail: "Username or password is incorrect. Please try again.",
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!username.trim()) { triggerShake(); return setError("__simple__:Please enter your username."); }
    if (!password)         { triggerShake(); return setError("__simple__:Please enter your password."); }

    if (username.trim().toLowerCase() === "admin") {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      triggerShake();
      setError("ADMIN_NOT_ALLOWED");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ username: username.trim(), password }),
      });
      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        if (data?.role && data.role.toLowerCase() === "admin") {
          const newAttempts = attempts + 1;
          setAttempts(newAttempts);
          triggerShake();
          setError("ADMIN_NOT_ALLOWED");
          setLoading(false);
          return;
        }
        setDemoAuthFlags();
        setShowSuccess(true);
      } else {
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        triggerShake();
        setError(data?.error || data?.message || "INVALID_CREDS");
      }
    } catch (err) {
      console.error("Login error:", err);
      triggerShake();
      setError("__simple__:Login failed. Check your network and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleStateConfirm = () => {
    if (!selectedState) return;
    localStorage.setItem("kyc_state", selectedState);
    navigate("/home", { replace: true });
  };

  const filteredStates = INDIAN_STATES.filter((s) =>
    s.toLowerCase().includes(stateSearch.toLowerCase())
  );

  const isSimpleError = typeof error === "string" && error.startsWith("__simple__:");
  const simpleErrorText = isSimpleError ? error.replace("__simple__:", "") : null;
  const richError = !isSimpleError && error ? getErrorMessage(error, attempts) : null;

  const teal   = "#40BEB4";
  const gold   = "#FFC850";
  const navy   = "#071520";
  const cardBg = "rgba(10, 25, 40, 0.92)";
  const border = "rgba(64,190,180,0.2)";
  const red    = "#ff4d6d";

  const styles = {
    wrapper: {
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "40px 20px 0",
      background: "linear-gradient(160deg, #071520 0%, #0a1e2e 35%, #071018 100%)",
      position: "relative",
      overflow: "hidden",
    },
    grid: {
      position: "absolute",
      inset: 0,
      backgroundImage: `
        linear-gradient(rgba(64,190,180,0.03) 1px, transparent 1px),
        linear-gradient(90deg, rgba(64,190,180,0.03) 1px, transparent 1px)
      `,
      backgroundSize: "48px 48px",
      pointerEvents: "none",
    },
    glow: {
      position: "absolute",
      inset: 0,
      background: `
        radial-gradient(ellipse at 20% 20%, rgba(64,190,180,0.05) 0%, transparent 50%),
        radial-gradient(ellipse at 80% 80%, rgba(255,200,80,0.04) 0%, transparent 50%)
      `,
      pointerEvents: "none",
    },
    card: {
      position: "relative",
      zIndex: 10,
      width: "100%",
      maxWidth: "400px",
      background: cardBg,
      border: `1px solid ${error ? "rgba(255,77,109,0.3)" : border}`,
      borderRadius: "20px",
      padding: "40px 36px 32px",
      boxShadow: error
        ? `0 0 0 1px rgba(255,255,255,0.03) inset, 0 20px 60px rgba(0,0,0,0.6), 0 0 40px rgba(255,77,109,0.08)`
        : `0 0 0 1px rgba(255,255,255,0.03) inset, 0 20px 60px rgba(0,0,0,0.6), 0 0 40px rgba(64,190,180,0.06)`,
      transition: "border-color 0.3s, box-shadow 0.3s",
      animation: shake ? "shakeError 0.55s ease" : "none",
    },
    cardAccent: {
      position: "absolute",
      top: 0,
      left: "40px",
      right: "40px",
      height: "1px",
      background: error
        ? `linear-gradient(90deg, transparent, ${red}80, ${red}50, transparent)`
        : `linear-gradient(90deg, transparent, ${teal}80, ${gold}50, transparent)`,
      borderRadius: "0 0 4px 4px",
      transition: "background 0.3s",
    },
    cornerBase: { position: "absolute", width: "20px", height: "20px", opacity: 0.45 },
    cornerTL: { top: "12px",    left: "12px",  borderTop:    `1.5px solid ${teal}`, borderLeft:  `1.5px solid ${teal}`, borderRadius: "4px 0 0 0" },
    cornerTR: { top: "12px",    right: "12px", borderTop:    `1.5px solid ${teal}`, borderRight: `1.5px solid ${teal}`, borderRadius: "0 4px 0 0" },
    cornerBL: { bottom: "12px", left: "12px",  borderBottom: `1.5px solid ${gold}`, borderLeft:  `1.5px solid ${gold}`, borderRadius: "0 0 0 4px" },
    cornerBR: { bottom: "12px", right: "12px", borderBottom: `1.5px solid ${gold}`, borderRight: `1.5px solid ${gold}`, borderRadius: "0 0 4px 0" },
    title: { fontSize: "22px", fontWeight: 500, color: "#fff", textAlign: "center", marginBottom: "6px", letterSpacing: "-0.3px" },
    titleAccent: { color: teal },
    subtitle: { fontSize: "13px", color: "rgba(255,255,255,0.4)", textAlign: "center", marginBottom: "32px", letterSpacing: "0.2px" },
    fieldWrap: { marginBottom: "18px" },
    label: { display: "block", fontSize: "11px", fontWeight: 500, color: "rgba(255,255,255,0.45)", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: "8px" },
    input: { width: "100%", padding: "13px 16px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "10px", color: "#fff", fontSize: "14px", outline: "none", transition: "border-color 0.2s, box-shadow 0.2s", boxSizing: "border-box" },
    inputFocused: { borderColor: "rgba(64,190,180,0.45)", boxShadow: "0 0 0 3px rgba(64,190,180,0.08)" },
    inputError: { borderColor: "rgba(255,77,109,0.45)", boxShadow: "0 0 0 3px rgba(255,77,109,0.08)" },
    passwordWrap: { position: "relative" },
    showBtn: { position: "absolute", right: "14px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", fontSize: "11px", fontWeight: 500, letterSpacing: "1px", textTransform: "uppercase", color: "rgba(255,200,80,0.7)" },
    row: { display: "flex", alignItems: "center", justifyContent: "space-between", margin: "16px 0 22px" },
    rememberLabel: { display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "rgba(64,190,180,0.8)", cursor: "pointer" },
    checkbox: { accentColor: teal },
    forgotBtn: { background: "none", border: "none", cursor: "pointer", fontSize: "13px", color: "rgba(255,255,255,0.35)" },
    simpleError: {
      display: "flex", alignItems: "center", gap: "8px",
      background: "rgba(255,77,109,0.1)", border: "1px solid rgba(255,77,109,0.25)",
      borderRadius: "8px", padding: "10px 13px", color: "#ff7a91",
      fontSize: "13px", marginBottom: "16px", animation: "fadeSlideIn 0.25s ease",
    },
    richError: {
      background: "linear-gradient(135deg, rgba(255,77,109,0.08) 0%, rgba(255,77,109,0.04) 100%)",
      border: "1px solid rgba(255,77,109,0.28)", borderRadius: "12px",
      padding: "14px 16px", marginBottom: "16px", animation: "fadeSlideIn 0.3s ease",
      position: "relative", overflow: "hidden",
    },
    richErrorStripe: { position: "absolute", left: 0, top: 0, bottom: 0, width: "3px", background: `linear-gradient(180deg, ${red}, #ff8fa3)`, borderRadius: "12px 0 0 12px" },
    richErrorHeader: { display: "flex", alignItems: "center", gap: "8px", marginBottom: "5px" },
    richErrorIcon: { fontSize: "16px", lineHeight: 1 },
    richErrorTitle: { fontSize: "13px", fontWeight: 600, color: "#ff7a91", letterSpacing: "0.1px" },
    richErrorDetail: { fontSize: "12px", color: "rgba(255,180,190,0.7)", lineHeight: 1.5, paddingLeft: "24px" },
    richErrorHint: { marginTop: "8px", paddingLeft: "24px", display: "flex", alignItems: "center", gap: "6px", fontSize: "11px", color: `rgba(64,190,180,0.7)`, letterSpacing: "0.3px" },
    submitBtn: { width: "100%", padding: "15px", background: `linear-gradient(135deg, #1a8a82 0%, ${teal} 50%, #1a9e94 100%)`, border: "none", borderRadius: "10px", color: navy, fontSize: "14px", fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase", cursor: "pointer", boxShadow: "0 4px 20px rgba(64,190,180,0.25)", transition: "transform 0.15s, box-shadow 0.15s" },
    submitBtnHover: { transform: "translateY(-1px)", boxShadow: "0 6px 28px rgba(64,190,180,0.35)" },
    submitBtnDisabled: { opacity: 0.65, cursor: "not-allowed", transform: "none" },
    dots: { display: "flex", justifyContent: "center", gap: "6px", marginTop: "24px" },
    dot: { width: "6px", height: "6px", borderRadius: "50%" },
    modalOverlay: {
      position: "fixed", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
      background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)", zIndex: 50,
    },
    modalCard: {
      background: "linear-gradient(160deg, #0d1f2d 0%, #071520 100%)",
      border: `1px solid rgba(64,190,180,0.3)`, borderRadius: "18px", padding: "36px 32px",
      textAlign: "center", maxWidth: "340px", width: "90%",
      boxShadow: `0 0 60px rgba(64,190,180,0.15)`,
    },
    modalTitle: { fontSize: "20px", fontWeight: 500, color: teal, marginBottom: "10px" },
    modalText: { color: "rgba(255,255,255,0.55)", marginBottom: "28px", fontSize: "14px" },
    okBtn: {
      padding: "12px 40px", fontSize: "14px", fontWeight: 700, color: navy,
      background: `linear-gradient(135deg, ${teal} 0%, #1a9e94 100%)`,
      border: "none", borderRadius: "8px", cursor: "pointer",
      textTransform: "uppercase", letterSpacing: "1px",
      boxShadow: `0 4px 16px rgba(64,190,180,0.3)`,
    },
    stateModalCard: {
      background: "linear-gradient(160deg, #0d1f2d 0%, #071520 100%)",
      border: `1px solid rgba(64,190,180,0.25)`, borderRadius: "20px",
      padding: "32px 28px 28px", maxWidth: "420px", width: "92%",
      boxShadow: `0 0 80px rgba(64,190,180,0.12), 0 30px 60px rgba(0,0,0,0.7)`,
      position: "relative",
    },
    stateModalAccent: {
      position: "absolute", top: 0, left: "30px", right: "30px", height: "2px",
      background: `linear-gradient(90deg, transparent, ${teal}, ${gold}, transparent)`,
      borderRadius: "0 0 4px 4px",
    },
    stateModalIcon: {
      width: "52px", height: "52px", borderRadius: "50%",
      background: "rgba(64,190,180,0.1)", border: `1.5px solid rgba(64,190,180,0.3)`,
      margin: "0 auto 14px", display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: "22px",
    },
    stateModalTitle: { fontSize: "19px", fontWeight: 600, color: "#fff", textAlign: "center", marginBottom: "4px" },
    stateModalSub: { fontSize: "13px", color: "rgba(255,255,255,0.4)", textAlign: "center", marginBottom: "20px" },
    stateSearchInput: {
      width: "100%", padding: "11px 14px", background: "rgba(255,255,255,0.05)",
      border: `1px solid rgba(64,190,180,0.2)`, borderRadius: "10px",
      color: "#fff", fontSize: "13px", outline: "none", boxSizing: "border-box",
      marginBottom: "12px",
    },
    stateList: {
      maxHeight: "200px", overflowY: "auto", marginBottom: "20px",
      borderRadius: "10px", border: "1px solid rgba(255,255,255,0.07)",
    },
    stateItem: (isSelected) => ({
      padding: "10px 14px", cursor: "pointer", fontSize: "13px",
      color: isSelected ? navy : "rgba(255,255,255,0.8)",
      background: isSelected ? teal : "transparent",
      borderBottom: "1px solid rgba(255,255,255,0.05)",
      transition: "background 0.15s",
      display: "flex", alignItems: "center", justifyContent: "space-between",
    }),
    stateConfirmBtn: {
      width: "100%", padding: "13px", fontSize: "14px", fontWeight: 700,
      color: selectedState ? navy : "rgba(255,255,255,0.3)",
      background: selectedState
        ? `linear-gradient(135deg, #1a8a82 0%, ${teal} 100%)`
        : "rgba(255,255,255,0.05)",
      border: selectedState ? "none" : "1px solid rgba(255,255,255,0.1)",
      borderRadius: "10px", cursor: selectedState ? "pointer" : "not-allowed",
      letterSpacing: "1px", textTransform: "uppercase",
      boxShadow: selectedState ? "0 4px 20px rgba(64,190,180,0.3)" : "none",
      transition: "all 0.2s",
    },
    selectedBadge: {
      display: "inline-flex", alignItems: "center", gap: "6px",
      background: "rgba(64,190,180,0.15)", border: `1px solid rgba(64,190,180,0.3)`,
      borderRadius: "20px", padding: "4px 12px", fontSize: "12px", color: teal,
      marginBottom: "14px",
    },
  };

  const hasError = !!error;

  return (
    <div style={styles.wrapper}>
      <div style={styles.grid} />
      <div style={styles.glow} />

      {/* ── LOGIN CARD ── */}
      <div style={styles.card}>
        <div style={styles.cardAccent} />
        <div style={{ ...styles.cornerBase, ...styles.cornerTL }} />
        <div style={{ ...styles.cornerBase, ...styles.cornerTR }} />
        <div style={{ ...styles.cornerBase, ...styles.cornerBL }} />
        <div style={{ ...styles.cornerBase, ...styles.cornerBR }} />

        {/* Icon */}
        <div style={{
          width: "80px", height: "80px", borderRadius: "50%",
          margin: "0 auto 20px",
          background: "linear-gradient(135deg, rgba(64,190,180,0.15) 0%, rgba(64,190,180,0.05) 100%)",
          border: "1.5px solid rgba(64,190,180,0.4)",
          boxShadow: "0 0 0 6px rgba(64,190,180,0.06), 0 0 24px rgba(64,190,180,0.15)",
          display: "flex", alignItems: "center", justifyContent: "center",
          position: "relative", overflow: "visible",
        }}>
          <div style={{
            position: "absolute", width: "96px", height: "96px", borderRadius: "50%",
            border: "1px dashed rgba(64,190,180,0.2)",
            animation: "spin 12s linear infinite",
          }} />
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="2" y="6" width="13" height="12" rx="2" fill="rgba(64,190,180,0.15)" stroke="#40BEB4" strokeWidth="1.5"/>
            <path d="M15 10l5-3v10l-5-3V10z" fill="rgba(64,190,180,0.15)" stroke="#40BEB4" strokeWidth="1.5" strokeLinejoin="round"/>
            <circle cx="8" cy="12" r="1.5" fill="#40BEB4" opacity="0.6"/>
          </svg>
        </div>

        <h2 style={styles.title}>
          Video <span style={styles.titleAccent}>KYC</span> — Portal
        </h2>
        <p style={styles.subtitle}>Sign in with your User credentials to begin</p>

        <form onSubmit={handleSubmit}>
          <div style={styles.fieldWrap}>
            <label style={styles.label}>Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => { setUsername(e.target.value); setError(""); }}
              onFocus={() => setInputFocus({ ...inputFocus, username: true })}
              onBlur={() => setInputFocus({ ...inputFocus, username: false })}
              placeholder="User"
              style={{
                ...styles.input,
                ...(inputFocus.username ? styles.inputFocused : {}),
                ...(hasError ? styles.inputError : {}),
              }}
            />
          </div>

          <div style={styles.fieldWrap}>
            <label style={styles.label}>Password</label>
            <div style={styles.passwordWrap}>
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(""); }}
                onFocus={() => setInputFocus({ ...inputFocus, password: true })}
                onBlur={() => setInputFocus({ ...inputFocus, password: false })}
                placeholder="••••••••••"
                style={{
                  ...styles.input,
                  paddingRight: "60px",
                  ...(inputFocus.password ? styles.inputFocused : {}),
                  ...(hasError ? styles.inputError : {}),
                }}
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} style={styles.showBtn}>
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          <div style={styles.row}>
            <label style={styles.rememberLabel}>
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} style={styles.checkbox} />
              Remember me
            </label>
            <button type="button" style={styles.forgotBtn}>Forgot?</button>
          </div>

          {isSimpleError && (
            <div style={styles.simpleError}>
              <span style={{ fontSize: "14px" }}>⚠</span>
              <span>{simpleErrorText}</span>
            </div>
          )}

          {richError && (
            <div style={styles.richError}>
              <div style={styles.richErrorStripe} />
              <div style={styles.richErrorHeader}>
                <span style={styles.richErrorIcon}>{richError.icon}</span>
                <span style={styles.richErrorTitle}>{richError.title}</span>
              </div>
              <div style={styles.richErrorDetail}>{richError.detail}</div>
              <div style={styles.richErrorHint}>
                <span style={{ fontSize: "10px" }}>●</span>
                Enter new credentials and try again
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            onMouseEnter={() => setButtonHover(true)}
            onMouseLeave={() => setButtonHover(false)}
            style={{
              ...styles.submitBtn,
              ...(buttonHover && !loading ? styles.submitBtnHover : {}),
              ...(loading ? styles.submitBtnDisabled : {}),
            }}
          >
            {loading ? "Verifying…" : "Sign in"}
          </button>
        </form>

        <div style={styles.dots}>
          <div style={{ ...styles.dot, background: "#40BEB4", boxShadow: "0 0 6px #40BEB4" }} />
          <div style={{ ...styles.dot, background: "rgba(64,190,180,0.4)" }} />
          <div style={{ ...styles.dot, background: "rgba(255,200,80,0.4)" }} />
          <div style={{ ...styles.dot, background: "#FFC850", boxShadow: "0 0 6px #FFC850" }} />
        </div>
      </div>

      {/* ── FOOTER LINKS (below card, above bottom edge) ── */}
      <div
        style={{
          position: "relative",
          zIndex: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          flexWrap: "wrap",
          marginTop: "28px",
          marginBottom: "28px",
          paddingBottom: "8px",
        }}
      >
        {[
          { to: "/privacy-policy", label: "Privacy Policy" },
          { to: "/terms",          label: "Terms & Conditions" },
          { to: "/contact",        label: "Contact Us" },
        ].map((link, idx, arr) => (
          <React.Fragment key={link.to}>
            <Link
              to={link.to}
              style={{
                color: "#40BEB4",
                textDecoration: "none",
                fontSize: "12px",
                fontWeight: 500,
                letterSpacing: "0.3px",
                opacity: 0.7,
                transition: "opacity 0.2s",
              }}
              onMouseEnter={(e) => (e.target.style.opacity = 1)}
              onMouseLeave={(e) => (e.target.style.opacity = 0.7)}
            >
              {link.label}
            </Link>
            {idx < arr.length - 1 && (
              <span style={{ color: "rgba(64,190,180,0.25)", fontSize: "12px" }}>|</span>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* ── SUCCESS MODAL ── */}
      {showSuccess && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalCard}>
            <h2 style={styles.modalTitle}>Successfully Logged In!</h2>
            <p style={styles.modalText}>Click OK to select your operating state.</p>
            <button
              onClick={() => { setShowSuccess(false); setShowStateSelect(true); }}
              style={styles.okBtn}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* ── STATE SELECT MODAL ── */}
      {showStateSelect && (
        <div style={styles.modalOverlay}>
          <div style={styles.stateModalCard}>
            <div style={styles.stateModalAccent} />
            <div style={styles.stateModalIcon}>🗺️</div>
            <h2 style={styles.stateModalTitle}>Select Your State</h2>
            <p style={styles.stateModalSub}>
              Choose the state where this KYC session will be conducted.
            </p>
            {selectedState && (
              <div style={{ textAlign: "center" }}>
                <span style={styles.selectedBadge}>
                  <span>✓</span> {selectedState}
                </span>
              </div>
            )}
            <input
              type="text"
              placeholder="Search state..."
              value={stateSearch}
              onChange={(e) => setStateSearch(e.target.value)}
              style={styles.stateSearchInput}
            />
            <div style={styles.stateList}>
              {filteredStates.length === 0 ? (
                <div style={{ padding: "16px", textAlign: "center", color: "rgba(255,255,255,0.35)", fontSize: "13px" }}>
                  No states found
                </div>
              ) : (
                filteredStates.map((state) => (
                  <div
                    key={state}
                    onClick={() => setSelectedState(state)}
                    style={styles.stateItem(selectedState === state)}
                  >
                    <span>{state}</span>
                    {selectedState === state && <span style={{ fontSize: "12px" }}>✓</span>}
                  </div>
                ))
              )}
            </div>
            <button
              disabled={!selectedState}
              onClick={handleStateConfirm}
              style={styles.stateConfirmBtn}
            >
              {selectedState ? `Proceed with ${selectedState}` : "Select a State to Continue"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoLogin;
